/**
 * <Sheet> — side-anchored panel built on Radix Dialog primitives.
 *
 * The pattern modern B2B SaaS uses for "click a row → see/edit detail
 * without leaving the list" (Linear, Notion, Airtable, Stripe, HubSpot,
 * Attio). The list page stays mounted behind the sheet so list context
 * is preserved + the user can browse neighbours quickly.
 *
 * Default placement: right side, 480px wide on desktop, full-width on
 * mobile. The host controls `open` + `onOpenChange` (matching the rest
 * of the Radix-derived primitives). Closing via Esc / overlay click /
 * the close button all fire onOpenChange(false).
 *
 * Per ADR-030 glass surface tokens. WCAG 2.2 AA: focus trap inside the
 * sheet while open (Radix gives this for free), focus restored to the
 * trigger on close, Esc + backdrop click both dismiss.
 */

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from "react";

import { cn } from "../lib/utils";

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;
export const SheetClose = DialogPrimitive.Close;
export const SheetPortal = DialogPrimitive.Portal;

const SheetOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50",
      // Very light dim — the table behind should stay faintly visible
      // through the sheet's glass + this overlay, so the user retains
      // list context (the whole reason for using a sheet over a modal).
      "bg-[oklch(40%_0.04_60_/_0.18)]",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
      className,
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

/* Growth Fusion: frosted glass lives ONLY here. The slide-over floats
   over the table at ~62% white alpha with a light backdrop blur, so
   the table edge is faintly visible behind the sheet. White hairline
   border keeps it crisp. Other surfaces in the cockpit are solid. */
const sheetVariants = cva(
  [
    "fixed z-50 flex flex-col gap-3",
    "bg-[var(--color-glass-surface-deep)] backdrop-blur-[var(--blur-glass)]",
    "border-[var(--color-glass-border)] shadow-[var(--shadow-glass-deep)]",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
    "duration-200",
  ].join(" "),
  {
    variants: {
      side: {
        right: [
          "inset-y-0 right-0 h-full w-full border-l sm:max-w-[480px] md:max-w-[560px] lg:max-w-[640px]",
          "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
        ].join(" "),
        left: [
          "inset-y-0 left-0 h-full w-full border-r sm:max-w-[480px] md:max-w-[560px]",
          "data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
        ].join(" "),
        bottom: [
          "inset-x-0 bottom-0 w-full border-t max-h-[85vh]",
          "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
        ].join(" "),
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

export interface SheetContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /** Set false to hide the built-in close button (rare). */
  showCloseButton?: boolean;
  /** Passed to the backdrop — a test id, or a class for a host that needs
   *  a different dim (the cockpit's phone menu drawer, SHELL-01). */
  overlayProps?: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> &
    Partial<Record<`data-${string}`, string>>;
}

export const SheetContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(({ className, children, side, showCloseButton = true, overlayProps, ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay {...overlayProps} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {children}
      {showCloseButton ? (
        <DialogPrimitive.Close
          aria-label="Close"
          data-testid="sheet-close-button"
          className={cn(
            "absolute right-3 top-3 grid place-items-center w-8 h-8 rounded-[var(--radius-sm)]",
            "text-[var(--color-text-on-glass-soft)] hover:text-[var(--color-text-on-glass-strong)]",
            "hover:bg-[oklch(0%_0_0_/_0.06)] transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand)]",
          )}
        >
          <X size={18} aria-hidden="true" />
        </DialogPrimitive.Close>
      ) : null}
    </DialogPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = "SheetContent";

// Slot components mirror the Dialog API so module pages can compose
// header + body + footer cleanly.
export function SheetHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 px-5 pt-5 pb-3 border-b border-[var(--color-glass-border)]",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SheetTitle({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Title>): ReactNode {
  return (
    <DialogPrimitive.Title
      className={cn("text-lg font-semibold tracking-tight", className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  );
}

export function SheetDescription({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Description>): ReactNode {
  return (
    <DialogPrimitive.Description
      className={cn(
        "text-sm text-[var(--color-text-on-glass-soft)]",
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Description>
  );
}

export function SheetBody({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={cn(
        "flex-1 overflow-y-auto px-5 py-4",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SheetFooter({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--color-glass-border)]",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
